import { fail } from "../../utils/Assert";
import FlumpLayer from "./FlumpLayer";
import FlumpMovieSymbol from "../symbols/FlumpMovieSymbol";
import FlumpLibrary from "../FlumpLibrary";

/**
 * TODO - Add description.
 */
export default class FlumpClip extends Phaser.Image
{
    /**
     * @param {Phaser.Game} game - A reference to the currently running game.
     * @param {FlumpMovieSymbol[]} movieClips - List of movie clips.
     * @param {FlumpLibrary} flumpLibrary - FlumpLibrary.
     * @param {string} [defaultClip=null] - Default clip to play.
     */
    constructor(game, movieClips, flumpLibrary, defaultClip = null)
    {
        super(game);

        /** @type {FlumpMovieSymbol[]} */
        this.movieClips = movieClips;
        ///** @type {FlumpMovieSymbol[]} */
        //this.movieClipsMaster = movieClips;
        /** @type {FlumpLibrary} */
        this.flumpLibrary = flumpLibrary;

        /** @type {string} */
        this.name = "FlumpClip";

        this.layers = [];
        this.currentLayers = [];
        this.subSprites = [];
        this.subClips = [];

        /** @type {FlumpMovieSymbol} */
        this.currentClip = null;

        /** @type {number} */
        this.playbackSpeed = 1;

        /** @type {number} */
        this.looping = 0;
        /** @type {boolean} */
        this._paused = false;
        /** @type {number} */
        this.framePosition = 0;
        /** @type {number} */
        this.duration = 0;

        /** @type {string} */
        this.fallbackLoop = null;

        /** @type {number} */
        this.skewX = 0;
        /** @type {number} */
        this.skewY = 0;

        /** @type {Phaser.Signal} */
        this.onComplete = new Phaser.Signal();
        /** @type {Phaser.Signal} */
        this.onLoop = new Phaser.Signal();

        if(defaultClip != null)
        {
            this.playClip(defaultClip);
        }
    }

    /**
     * Cleans up the FlumpClip.
     * @param {boolean} [destroyChildren=true] Defaulfs to true
     */
    destroy(destroyChildren = true)
    {
        this.flumpLibrary = null;
        this.movieClips = null;
        //this.movieClipsMaster = null;
        this.currentClip = null;

        this.layers = null;
        this.currentLayers = null;
        this.subSprites = null;
        this.subClips = null;

        if (this.onComplete != null)
        {
            this.onComplete.dispose();
            this.onComplete = null;
        }
        if (this.onLoop != null)
        {
            this.onLoop.dispose();
            this.onLoop = null;
        }

        super.destroy(destroyChildren);
    }

    /**
     * Play a new clip by config. This method should be used in most cases.
     * @param {object} options - Options with params for animation.
     * @param {number} options.clip - Animation to play.
     * @param {number} [options.looping=0] - Number of times to loop animation, -1 for infinite.
     * @param {boolean} [options.restart=false] - If the animation will restart if called while it is currently playing.
     * @param {string} [options.fallback=null] - Animation to fallback to.
     * @param {function} [options.callback=null] - Function to be called on animation complete.
     */
    play(options)
    {
        var defaults =
        {
            clip: null,
            looping: 0,
            restart: false,
            fallback: null,
            callback: null
        };

        options = this.setDefaults(options, defaults);

        if((this.currentClip != null && options.clip != this.currentClip.name) || options.restart)
        {
            this.looping = options.looping;

            this.onComplete.removeAll();

            if(options.callback)
            {
                this.onComplete.addOnce(options.callback.listener, options.callback.context);
            }

            if(this.looping != 0 && this.fallbackLoop == null)
            {
                this.fallbackLoop = this.currentClip.name;
            }
            else
            {
                this.fallbackLoop = options.fallback;
            }

            this.playClip(options.clip);
        }
    }

    /**
     * Play a clip once with a simple fallback.
     * @param {number} newClip - Animation to play.
     * @param {string} [fallback=false] - Should the animation fallback to its current looping animation.
     * @param {boolean} [restart=true] - If the animation will restart if called while it is currently playing.
     */
    playOnce(newClip, fallback = false, restart = true)
    {
        if((this.currentClip != null && newClip != this.currentClip.name) || restart)
        {
            if(fallback == false)
            {
                this.fallbackLoop = null;
            }

            this.looping = 0;
            this.playClip(newClip);
        }
    }

    /**
     * Loop a clip infinitly.
     * @param {number} newClip - Animation to play.
     * @param {boolean} [restart=true] - If the animation will restart if called while it is currently playing.
     */
    loop(newClip, restart = true)
    {
        if((this.currentClip != null && newClip != this.currentClip.name) || this.currentClip == null || restart)
        {
            this.looping = -1;
            this.playClip(newClip);
            this.fallbackLoop = this.currentClip.name;
        }
    }

    /** @internal */
    update()
    {
        super.update();

        if(this._paused || this.currentClip == null) return;

        if(this.currentClip.name == "transition_grover_still" || this.currentClip.name == "transition_groverAlt_still")
        {
            this.framePosition += this.game.time.physicsElapsedMS/1000 * this.playbackSpeed;
        }
        else
        {
            this.framePosition += this.game.time.elapsedMS/1000 * this.playbackSpeed;
        }

        var newFrame = this.framePosition * this.currentClip.frameRate;

        if (this.framePosition > this.duration - 1/this.currentClip.frameRate * 0.5)
        {
            this.framePosition = this.framePosition % this.duration;

            if(this.looping == 0)
            {
                if (this.fallbackLoop == null)
                {
                    this.framePosition = this.duration - 1/this.currentClip.frameRate;
                    newFrame = this.framePosition * this.currentClip.frameRate;
                    this.updateSubSprites(newFrame);

                    this._paused = true;
                }
                else
                {
                    this.loop(this.fallbackLoop);
                    this.fallbackLoop = null;
                }
                this.onComplete.dispatch();
            }
            else
            {
                if(this.looping > 0)
                {
                    this.looping--;
                }
                this.onLoop.dispatch();
            }
        }
        else
        {
            this.updateSubSprites(newFrame);
        }
    }

    updateSubSprites(newFrame)
    {
        for(var i = 0; i < this.subSprites.length; i++)
        {
            if(this.subSprites[i].enabled)
            {
                this.subSprites[i].goto(newFrame, this.playbackSpeed);
            }
        }

        for(var i = 0; i < this.subClips.length; i++)
        {
            if(this.subClips[i].enabled)
            {
                this.subClips[i].update();
                this.subClips[i].goto(newFrame, this.playbackSpeed);
            }
        }
    }

    /**
     * NOT WORKING
     * Swaps textures around. Takes an array, 0 is swap with 1, 2 with 3 ect...
     * @param {string[]} spritesToSwap - List of sprite to swap.
     */
    // swapSprites(spritesToSwap)
    // {
    //     for(var key in this.movieClipsMaster)
    //     {
    //         for(var j = 0; j < this.movieClipsMaster[key].layers.length; j++)
    //         {
    //             for(var k = 0; k < this.movieClipsMaster[key].layers[j].keyframes.length; k++)
    //             {
    //                 for(var l = 0; l < spritesToSwap.length; l+=2)
    //                 {
    //                     if(this.movieClipsMaster[key].layers[j].keyframes[k].symbolName == spritesToSwap[l])
    //                     {
    //                         this.movieClips[key].layers[j].keyframes[k].symbolName = spritesToSwap[l+1];
    //                     }
    //                 }
    //             }
    //         }
    //     }
    // }

    /** @type {boolean} */
    get paused()
    {
        return this._paused;
    }

    /** @type {boolean} */
    set paused(val)
    {
        this._paused = val;

        for(var i = 0; i < this.subSprites.length; i++)
        {
            this.subSprites[i].paused = this._paused;
        }

        for(var i = 0; i < this.subClips.length; i++)
        {
            this.subClips[i].paused(this._paused);
        }
    }

    /** @internal*/
    playClip(newClip)
    {
        var movieClip = this.movieClips[newClip];

        if(movieClip == null)
        {
            fail(`Animation ${newClip} not found.`);
            return;
        }

        var subClipCount = 0;
        var subSpriteCount = 0;

        for(var i = 0; i < movieClip.layers.length; i++)
        {
            if(this.isSubClip(movieClip.layers[i]))
            {
                if(subClipCount < this.subClips.length)
                {
                    this.subClips[subClipCount].setupClip(movieClip.layers[i], movieClip);
                    this.setChildIndex(this.subClips[subClipCount], i);
                }
                else
                {
                    var subClip = this.flumpLibrary.createSubClip(movieClip.layers[i].keyframes[0].symbolName, movieClip.layers[i], movieClip);
                    subClip.visible = false;

                    this.subClips.push(subClip);
                    this.addChild(subClip);
                    this.setChildIndex(subClip, i);
                }

                subClipCount++;
            }
            else
            {
                if(subSpriteCount < this.subSprites.length)
                {
                    this.subSprites[subSpriteCount].setupLayer(movieClip.layers[i], movieClip);
                    this.setChildIndex(this.subSprites[subSpriteCount], i);
                }
                else
                {
                    var subSprite = new FlumpLayer(this.game, movieClip.layers[i], movieClip, this.flumpLibrary)
                    this.subSprites.push(subSprite);
                    this.addChild(subSprite);
                    this.setChildIndex(subSprite, i);
                }

                subSpriteCount++;
            }
        }

        if(this.subClips.length > subClipCount)
        {
            for(var i = subClipCount; i < this.subClips.length; i++)
            {
                this.subClips[i].setActive(false);
            }
        }

        if(this.subSprites.length > subSpriteCount)
        {
            for(var i = subSpriteCount; i < this.subSprites.length; i++)
            {
                this.subSprites[i].setActive(false);
            }
        }

        this._paused = false;
        this.duration = movieClip.duration;
        this.framePosition = 0;
        this.currentClip = movieClip;
    }

    /** @internal */
    isSubClip(layer)
    {
        for(var i = 0; i < layer.keyframes.length; i++)
        {
            if(layer.keyframes[i].symbolName != null && !this.checkSpriteExist(layer.keyframes[i].symbolName))
            {
                return true;
            }
        }

        return false;
    }

    /** @internal */
    checkSpriteExist(spriteName)
    {
        return this.game.cache.checkBitmapDataKey(spriteName);
    }

    /** @internal */
    setDefaults(options, defaults)
    {
        for (var property in defaults)
        {
            if(defaults.hasOwnProperty(property))
            {
                if(options[property] == null)
                {
                    options[property] = defaults[property];
                }
            }
        }
        return options;
    }

    /** @internal */
    displayObjectUpdateTransform(parent)
    {
        if (!parent && !this.parent && !this.game)
        {
            return;
        }

        var p = this.parent;

        if (parent)
        {
            p = parent;
        }
        else if (!this.parent)
        {
            p = this.game.world;
        }

        // create some matrix refs for easy access
        var pt = p.worldTransform;
        var wt = this.worldTransform;

        // temporary matrix variables
        var a, b, c, d, tx, ty;

        // so if rotation is between 0 then we can simplify the multiplication process..
        if (this.rotation % PIXI.PI_2 || this.skewX || this.skewY)
        {
            // check to see if the rotation is the same as the previous render. This means we only need to use sin and cos when rotation actually changes
            // if (this.rotation !== this.rotationCache)
            // {
            //     this.rotationCache = this.rotation;
            //     this._sr = Math.sin(this.rotation);
            //     this._cr = Math.cos(this.rotation);
            // }

            // get the matrix values of the displayobject based on its transform properties..
            a  =  this.scale.x * Math.cos(this.rotation + this.skewY);
            b  =  this.scale.x * Math.sin(this.rotation + this.skewY);
            c  =  this.scale.y * Math.sin(-this.rotation - this.skewX);
            d  =  this.scale.y * Math.cos(this.rotation + this.skewX);
            tx =  this.position.x;
            ty =  this.position.y;

            // check for pivot.. not often used so geared towards that fact!
            if (this.pivot.x || this.pivot.y)
            {
                tx -= this.pivot.x * a + this.pivot.y * c;
                ty -= this.pivot.x * b + this.pivot.y * d;
            }

            // concat the parent matrix with the objects transform.
            wt.a  = a  * pt.a + b  * pt.c;
            wt.b  = a  * pt.b + b  * pt.d;
            wt.c  = c  * pt.a + d  * pt.c;
            wt.d  = c  * pt.b + d  * pt.d;
            wt.tx = tx * pt.a + ty * pt.c + pt.tx;
            wt.ty = tx * pt.b + ty * pt.d + pt.ty;
        }
        else
        {
            // lets do the fast version as we know there is no rotation..
            a  = this.scale.x;
            d  = this.scale.y;

            tx = this.position.x - this.pivot.x * a;
            ty = this.position.y - this.pivot.y * d;

            wt.a  = a  * pt.a;
            wt.b  = a  * pt.b;
            wt.c  = d  * pt.c;
            wt.d  = d  * pt.d;
            wt.tx = tx * pt.a + ty * pt.c + pt.tx;
            wt.ty = tx * pt.b + ty * pt.d + pt.ty;
        }

        // multiply the alphas..
        /** @ignore */
        this.worldAlpha = this.alpha * p.worldAlpha;

        this.worldPosition.set(wt.tx, wt.ty);
        this.worldScale.set(Math.sqrt(wt.a * wt.a + wt.b * wt.b), Math.sqrt(wt.c * wt.c + wt.d * wt.d));
        /** @ignore */
        this.worldRotation = Math.atan2(-wt.c, wt.d);

        // reset the bounds each time this is called!
        this._currentBounds = null;

        //  Custom callback?
        if (this.transformCallback)
        {
            this.transformCallback.call(this.transformCallbackContext, wt, pt);
        }
    }
}
